import React from 'react';
import { Info } from 'lucide-react';
import { usePaymentMode } from '../contexts/PaymentModeContext';

/**
 * HostDirectModeBanner
 * Small, non-intrusive banner that renders ONLY when PAYMENT_MODE=host_direct.
 * Surfaces the "advisory mode" message on booking and statement screens so
 * hosts and renters understand HireHub is not processing payments.
 *
 * Variants:
 *   - 'default'  (blue info, concise)  — for BookingDetails top
 *   - 'subtle'   (slate, minimal)       — for statements / dashboards
 */
export const HostDirectModeBanner = ({
  variant = 'default',
  context = 'booking',
  className = '',
  ...rest
}) => {
  const { isHostDirect, loaded } = usePaymentMode();

  if (!loaded || !isHostDirect) return null;

  const subtitleByContext = {
    booking: 'HireHub does not process payments for this booking. Payment is arranged directly between host and renter.',
    statements: 'HireHub does not process payments in this mode. Historical platform-fee entries remain for previously completed bookings.',
    payouts: 'HireHub does not process payouts in this mode. Hosts are paid directly by renters.',
    generic: 'HireHub does not process payments in this mode. Payment is arranged directly between host and renter.'
  };
  const subtitle = subtitleByContext[context] || subtitleByContext.generic;

  if (variant === 'subtle') {
    return (
      <div
        className={`flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 ${className}`}
        data-testid="host-direct-advisory-banner"
        {...rest}
      >
        <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
        <div>
          <span className="font-medium text-slate-700">Direct payment mode.</span>{' '}
          <span>{subtitle}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-4 py-3 ${className}`}
      data-testid="host-direct-advisory-banner"
      {...rest}
    >
      <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <p className="font-semibold">Direct payment mode</p>
        <p className="text-blue-800/90">{subtitle}</p>
      </div>
    </div>
  );
};

export default HostDirectModeBanner;
